import { useCallback, useMemo } from 'react';
import { allowsAnywhere } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import { AccountTab } from './account';
import { GuardsTab } from './guards';
import { makeLoyaltyT, type LoyaltyKey } from './i18n';
import { ReconciliationTab } from './reconciliation';
import { ReferralsTab } from './referrals';
import { RulesTab } from './rules';
import { SuggestionsTab } from './suggestions';
import { TiersTab } from './tiers';
import { ModulePage } from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { useTab } from '../../shell/hooks';

type TabKey =
  | 'rules'
  | 'suggestions'
  | 'account'
  | 'tiers'
  | 'referrals'
  | 'guards'
  | 'reconciliation';

const TABS: ReadonlyArray<{ key: TabKey; label: LoyaltyKey }> = [
  { key: 'rules', label: 'tab.rules' },
  { key: 'suggestions', label: 'tab.suggestions' },
  { key: 'account', label: 'tab.account' },
  { key: 'tiers', label: 'tab.tiers' },
  { key: 'referrals', label: 'tab.referrals' },
  { key: 'guards', label: 'tab.guards' },
  { key: 'reconciliation', label: 'tab.reconciliation' },
];

export default function LoyaltyModule({ title, user }: ModuleViewProps) {
  const locale = user.locale;
  const t = useMemo(() => makeLoyaltyT(locale), [locale]);
  const [tab, setTab] = useTab<TabKey>(
    'loyalty',
    TABS.map((item) => item.key),
    'rules',
  );
  const toast = useToast();

  // Ошибка показывается рядом с действием, а не плашкой вверху страницы,
  // до которой от кнопки внизу формы надо было доскроллить.
  const onError = useCallback(
    (message: string) => {
      if (message) toast.error(message);
    },
    [toast],
  );

  const canManage = allowsAnywhere(user, 'loyalty.rule.manage');

  return (
    <ModulePage
      title={title}
      subtitle={t('module.subtitle')}
      tabs={TABS.map((item) => ({ key: item.key, label: t(item.label) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'rules' && <RulesTab t={t} onError={onError} />}
      {tab === 'suggestions' && (
        <SuggestionsTab t={t} canManage={canManage} onError={onError} />
      )}
      {tab === 'account' && <AccountTab t={t} locale={locale} onError={onError} />}
      {tab === 'tiers' && <TiersTab t={t} onError={onError} canManage={canManage} />}
      {tab === 'referrals' && <ReferralsTab t={t} canManage={canManage} onError={onError} />}
      {tab === 'guards' && <GuardsTab t={t} canManage={canManage} onError={onError} />}
      {tab === 'reconciliation' && <ReconciliationTab t={t} onError={onError} />}
    </ModulePage>
  );
}
